import { ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import Icon from '@apigrowfinance/pbolt-icon';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import dayjs from 'dayjs';
import { RiBillLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import { StatementItemType } from '@clients/statementsClient';
import { useAccount } from '@contexts/AccountProvider';

export type StatementHeaderProps = {
    statement: StatementItemType;
    currencyCode: string;
};

const StatementHeader = ({ statement, currencyCode }: StatementHeaderProps) => {
    const { program } = useAccount();
    const statementDaysToPay = dayjs().to(statement.calendar?.due_date);
    const { theme } = useTheme();
    return (
        <ListItem
            data-testid="statement-header-component"
            sx={{ pl: 2 }}
            secondaryAction={
                <ListItemText
                    primaryTypographyProps={{
                        style: {
                            textAlign:
                                theme.direction === 'rtl' ? 'left' : 'right',
                        },
                    }}
                    primary={
                        <CurrencyFormatter
                            testId="statement-balance"
                            amount={statement.current_balance}
                            currency={currencyCode}
                            symbol={
                                program?.type === 'CREDITO'
                                    ? 'default'
                                    : 'positive'
                            }
                        />
                    }
                    secondary={
                        <FormattedMessage
                            id="AccountStatements.paymentDue"
                            defaultMessage="Payment due {message}"
                            values={{ message: statementDaysToPay }}
                        />
                    }
                />
            }>
            <ListItemAvatar sx={{ pl: '13px' }}>
                <Icon data-testid="bill-icon">
                    <RiBillLine />
                </Icon>
            </ListItemAvatar>
            <ListItemText
                sx={{
                    color: theme?.custom?.brand?.textMain || 'text.primary',
                    ml: 2,
                }}
                primary={
                    <FormattedMessage
                        id="AccountStatements.statement"
                        defaultMessage="Statement"
                    />
                }
                secondary={
                    <DateFormatter
                        date={statement?.calendar?.due_date}
                        format="monthYearFull"
                    />
                }
            />
        </ListItem>
    );
};

export default StatementHeader;
